<template>
  <AsidePanel
    ref="asidePanel"
    :components="components"
    :handleCancel="handleCancel"
    :handleMountedCallback="handleMounted"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :navigateToTab="activeTab"
    :noNotification="true"
    :saveText="'save user'"
    :viewport="viewport"
    @nav-change="handleNavChange"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="user"
        modifier="windows"
        :showModifier="item.externally_managed"
      />
    </template>
    <template v-slot:leftPaneCustomContent>
      <UserActivationSummaryModal
        v-if="showUserActivationSummaryModal"
        :item="item"
        :showModal="showUserActivationSummaryModal"
        @close="closeActivationSummaryModal"
      />
      <p
        v-if="item.email"
        :class="$style.subTitle"
        :title="item.email"
      >
        {{ item.email }}
      </p>
      <p
        v-if="item.jobTitle"
        :class="$style.subTitle"
      >
        {{ item.jobTitle }}
      </p>
      <p
        v-if="item.department"
        :class="$style.subTitle"
      >
        {{ item.department }}
      </p>
      <SeverityModal
        notificationTitle="This will alter who can log in to your devices."
        :primaryButtonProps="primaryButtonProps"
        :showModal="shouldShowDevicesModal"
        :title="userModalTitle"
        variant="sev2"
        @close="handleClose"
        @save="handleConfirmDeviceBindingChange"
      >
        <template v-slot:subtitle>
          <span>
            <b>Are you sure you want to change this user's assigned devices?</b>
          </span>
        </template>
        <template v-slot:body>
          <ParagraphBody>
            {{ userModalBody }}
          </ParagraphBody>
          <ParagraphBody>
            {{ userModalBody2 }}
          </ParagraphBody>
        </template>
        <template v-slot:learnmore>
          <ParagraphBody>
            For more information, see
            <Link
              :href="KBArticle"
              :isExternal="false"
              target="_blank"
              text="Binding Users to Devices"
            />
          </ParagraphBody>
        </template>
      </SeverityModal>
    </template>
    <template v-slot:status>
      <UserStatus
        v-if="isItemBeingUpdated"
        :item="previousItem"
      />
    </template>
  </AsidePanel>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  Link,
  ParagraphBody,
  SeverityModal,
  Types,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { notify } from 'client/util';
import ActivationEmailConfirmationModal from 'client/systemusers/Modals/ActivationEmailConfirmationModal.vue';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelMethodMixin from 'client/components/AsidePanel/mixins/AsidePanelMethodMixin';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import DirectoryItemTable from 'client/directories/components/DirectoryItemTable.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import ModalEventBus from 'components/Modal/ModalEventBus';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable.vue';
import SystemItemTableColumns from 'client/systems/SystemItemTable/SystemItemTableColumns';
import SystemUserUserGroups from 'client/systemusers/components/SystemUserUserGroups.vue';
import UserActivationSummaryModal from 'client/systemusers/Modals/UserActivationSummaryModal.vue';
import UserDetailsWrap from 'client/systemusers/UserDetails/UserDetailsWrap.vue';
import UserGroupSummaryDetails from 'client/components/ItemTable/BodyComponents/UserGroupSummary/UserGroupSummaryDetails.vue';
import UserHighlights from 'client/systemusers/UserHighlights/UserHighlights.vue';
import UserStatus from 'client/systemusers/UserDetails/UserStatus.vue';
import WelcomeEmailConfirmationModal from 'client/systemusers/Modals/WelcomeEmailConfirmationModal.vue';

const {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ModuleConsts,
  UsersConsts,
} = Constants;

const { error } = Constants.default;
const { create, update } = ItemActionConsts;
const { activated, staged, suspended } = ModuleConsts.systemUser.accountStates;
const { PrimaryButtonProps } = Types;

const settingsModel = 'SettingsModel';
const systemCollection = 'SystemCollection';
const systemUserCollection = 'SystemUserCollection';
const systemUserModel = 'SystemUserModel';
const systemUserWebAuthnKeysModel = 'SystemUserWebAuthnKeysModel';

export default {
  name: 'UserAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    Link,
    ParagraphBody,
    SeverityModal,
    UserStatus,
    UserActivationSummaryModal,
  },

  ModalEventBus,

  mixins: [AsidePanelMethodMixin, AsidePanelPropsMixin, RouteGuardMixin],

  props: {
    navigateToTab: {
      type: Object,
      default: () => {},
      validator(obj) {
        if (obj) {
          return typeof obj.tabLabel !== 'undefined';
        }
        return true;
      },
    },
  },

  data() {
    return {
      activationEmail: '',
      activeTab: this.navigateToTab,
      currentNavItem: '',
      components: [
        {
          iconClassName: 'jc-details',
          label: 'Details',
          vueComponent: UserDetailsWrap,
          vueComponentProps: {
            navigateToAsideTab: this.navigateToAsideTab,
          },
        },
        {
          backboneView: null,
          iconClassName: 'jc-groups-users',
          itemType: ItemTypeConsts.userGroup,
          label: 'User Groups',
          headerLabels: {
            [create]: 'Select User Groups to add membership for this user',
            [update]: 'is a member of the selected User Groups',
          },
          vueComponent: SystemUserUserGroups,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `${UsersConsts.APIPaths.users}${this.itemId}/memberof`
              : undefined,
            details: true,
            detailsButtonType: 'caret',
            detailsComponent: UserGroupSummaryDetails,
            detailsIconLeft: true,
            inDetailsAsidePanel: true,
            parentType: ItemTypeConsts.systemUser,
            showType: false,
            watchSelectedItems: true,
          },
        },
        {
          backboneView: null,
          iconClassName: 'jc-systems1',
          itemType: ItemTypeConsts.device,
          label: 'Devices',
          headerLabels: {
            [create]:
              'Accounts for this user will be enabled for the selected devices',
            [update]: 'has accounts on the selected devices',
          },
          vueComponent: SystemItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `${UsersConsts.APIPaths.users}${this.itemId}/systems`
              : undefined,
            columns: [
              SystemItemTableColumns.status,
              { ...SystemItemTableColumns.name, classNames: undefined },
              SystemItemTableColumns.os,
              {
                ...SystemItemTableColumns.sudo,
                bodyComponentProps: { showNewWorkflow: true, viewport: this.viewport },
              },
            ],
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.userAsidePanel,
          },
        },
        {
          iconClassName: 'jc-directories1',
          itemType: ItemTypeConsts.directory,
          label: 'Directories',
          headerLabels: {
            [create]: 'Select directories to enable for this user',
            [update]: 'has the selected directories enabled',
          },
          renderImmediately: true,
          vueComponent: DirectoryItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId
              ? `${UsersConsts.APIPaths.users}${this.itemId}/directories`
              : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.userAsidePanel,
          },
        },
      ],
      errorMessage:
        "Review the User's Details Tab for missing or invalid information",
      itemHasBeenSaved: false,
      isModalLoading: false,
      KBArticle: 'https://support.jumpcloud.com/s/article/Binding-Users-to-Devices',
      shouldOpenActivationEmailModal: false,
      shouldOpenWelcomeEmailModal: false,
      shouldOpenUserSummaryModal: false,
      shouldShowDevicesModal: false,
      showActivationEmailModal: false,
      showUserActivationSummaryModal: false,
      suspended,
      userModalActionLabel: '',
      userModalBody: '',
      userModalBody2: '',
      userModalSubtitle: '',
      userModalTitle: '',
    };
  },

  computed: {
    ...mapState(systemUserModel, [
      'item',
      'previousItem',
      'scheduledStateChangesForUser',
    ]),

    ...mapState('SettingsModule', {
      newSystemUserStateDefaults: (state) => state.newSystemUserStateDefaults,
    }),

    defaultState() {
      return get(this.newSystemUserStateDefaults, 'manualEntry', staged);
    },

    isNewUser() {
      return this.$route.params.id;
    },

    primaryButtonProps() {
      return PrimaryButtonProps({
        isLoading: false,
        isDisabled: false,
        text: this.userModalActionLabel,
        hasOutline: false,
        type: 'primary',
      });
    },
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  notify,

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(settingsModel, {
      fetchSettings: 'fetchItem',
      resetSettings: 'resetItem',
    }),

    ...mapActions(systemUserModel, [
      'fetchPublicKeys',
      'fetchScheduledStateChanges',
      'bindUserToUserGroups',
      'bindUserToSystems',
      'bindSudoStatus',
      'bindUserToDirectories',
      'resetItem',
      'saveItem',
      'savePublicKeys',
      'updatePasswordPolicy',
    ]),

    ...mapActions(systemUserWebAuthnKeysModel, {
      resetAuthnKeys: 'resetItem',
      saveWebAuthnKeys: 'saveWebAuthnKeys',
    }),

    ...mapActions(systemCollection, [
      'acknowledgeSelectedSystems',
      'resetDevicePanelBoundItemsPagination',
      'setUsePaginationMethodForBoundDevices',
    ]),

    ...mapActions(systemUserCollection, ['updateModifiedItemCount']),

    ...mapGetters(systemUserModel, [
      'displayName',
      'getSystemBindingChanges',
      'hasItemChanged',
      'haveAssociationsChanged',
      'havePublicKeysChanged',
      'haveSystemBindingsChanged',
      'isItemBeingUpdated',
    ]),

    ...mapGetters(systemUserWebAuthnKeysModel, {
      haveWebAuthnKeysChanged: 'hasItemChanged',
    }),

    checkToAddHighlightsTab() {
      const highlightsTab = {
        isNew: false,
        label: 'Highlights',
        vueComponent: UserHighlights,
        vueComponentProps: {
          itemId: this.itemId,
          navigateToAsideTab: this.navigateToAsideTab,
        },
      };

      // Add Highlights tab to the aside panel for an existing user
      if (this.isItemBeingUpdated() && this.item.state !== staged) {
        this.components.unshift(highlightsTab);
      }
    },

    closeActivationSummaryModal() {
      this.showUserActivationSummaryModal = false;
    },

    getMsgUserHasBeenSaved(item) {
      const userEditLink = `/#/users/${item.id}/details`;
      return `<a data-test-id="AppNotificationController__notifications" href="${userEditLink}">Edit user</a>`;
    },

    getMsgUserSettingNotificationSaved() {
      const message = 'We noticed you created this user in a state that is different from your organizations\'s default settings. Would you like to update the settings?';
      const settingsEditLink = '/#/settings/user';
      return `<div>${message}<br><br><a href="${settingsEditLink}">Go to User Settings</a></div>`;
    },

    getMsgUserSettingsHasBeenSaved(item) {
      const userEditLink = `/#/users/${item.id}/details`;
      const editMessage = this.item.state === staged
        ? 'This user account state is currently staged meaning they cannot access their account/resources or receive emails.'
        : 'This user account state is now active meaning they can access their account/resources and receive emails.';
      return `<div data-test-id="AppNotificationController__notifications">${editMessage}<br><br><a href="${userEditLink}">Edit User</a></div>`;
    },

    // Reset models on exit to avoid values being accidentally set
    handleBeforeDestroy() {
      this.resetAuthnKeys();
      this.resetItem();
      this.resetSettings();

      this.resetDevicePanelBoundItemsPagination();
    },

    /**
     * Let any components that are watching the list of users know that they can refresh.
     * This is only necessary on cancel - if the admin saves the user, the refresh
     * is automatically triggered.
     */
    handleCancel() {
      this.updateModifiedItemCount();
    },

    handleClose() {
      this.shouldShowDevicesModal = false;
    },

    async handleConfirmDeviceBindingChange() {
      try {
        await this.saveAndBindDeviceBindings();
        if (!this.shouldOpenUserSummaryModal) {
          this.triggerSuccessNotification({
            message: 'User saved successfully!',
          });
          this.$refs.asidePanel.redirect();
        }
      } catch (exp) {
        this.triggerErrorNotification({
          message: exp instanceof Error ? exp : Error('Unknown error'),
        });
      } finally {
        this.shouldShowDevicesModal = false;
      }
    },

    handleMounted() {
      this.fetchSettings();

      if (this.navigateToTab) {
        this.navigateToAsideTab(this.navigateToTab.tabLabel);
      }

      if (this.isItemBeingUpdated()) {
        this.fetchScheduledStateChanges();
        this.fetchPublicKeys().catch(() => this.$options.notify(error, "Unable to fetch user's public keys!"));
      }

      this.checkToAddHighlightsTab();

      const usePaginationMethodForBoundDevicesFF = this.$hasFeatureFlag(
        this.$featureFlags.usePaginationMethodForBoundDevices,
      );

      this.setUsePaginationMethodForBoundDevices(usePaginationMethodForBoundDevicesFF);
    },

    handleNavChange(label) {
      this.currentNavItem = label;
    },

    async handleSave() {
      const validator = this.$validator;

      await validator.validate();

      if (validator.errors.any()) {
        this.$options.notify('error', this.errorMessage);
        throw Error('Validation errors');
      }

      if (!this.item.password
          && !this.isItemBeingUpdated()
          && this.item.state === activated) {
        this.shouldOpenActivationEmailModal = true;
      }

      if (this.item.password
        && !this.isItemBeingUpdated()
        && this.item.state !== staged) {
        this.shouldOpenWelcomeEmailModal = true;
      }

      // Display the user activation summary modal for new staged users
      // or existing staged users without any scheduled activations
      const events = this.scheduledStateChangesForUser
        .filter(eventData => eventData.state === activated);

      if (this.item.state === staged) {
        this.shouldOpenUserSummaryModal = !this.isItemBeingUpdated()
          ? true
          : !events.length;
      }

      this.itemHasBeenSaved = true;

      // `haveSystemBindingsChanged` will check if device associations have changed
      if (!this.haveSystemBindingsChanged()) {
        await this.saveAndBindDeviceBindings();
      } else {
        this.setUserBindingModalText();
        this.shouldShowDevicesModal = true;
      }

      return new SaveReturnValue({
        canContinue: !(this.haveSystemBindingsChanged() || this.shouldOpenUserSummaryModal),
        noNotification: (this.haveSystemBindingsChanged() || this.shouldOpenUserSummaryModal),
      });
    },

    async handleSaveItem() {
      /**
       * With alternate email available,
       * the activation or welcome email will be sent in a later
       * step AFTER the user is saved
       */
      const shouldSendEmail = false;

      const item = await this.saveItem(shouldSendEmail);
      await this.saveSuccess(item);
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }

      return (
        this.hasItemChanged()
        || this.haveWebAuthnKeysChanged()
        || this.havePublicKeysChanged()
        || this.haveAssociationsChanged()
      );
    },

    navigateToAsideTab(tabLabel) {
      this.activeTab = { tabLabel };
    },

    openActivationEmailModal() {
      this.$options.ModalEventBus.open({
        component: ActivationEmailConfirmationModal,
        componentProps: {
          additionalText: 'You may also skip sending this email now, and send it at a later time.',
          defaultInputValue: this.item.alternateEmail || this.item.email,
          cancelText: 'skip',
        },
        preventCloseOnOverlayClick: true,
      });
    },

    openConfirmationModals() {
      if (this.shouldOpenActivationEmailModal) {
        this.openActivationEmailModal();
      } else if (this.shouldOpenWelcomeEmailModal) {
        this.openWelcomeEmailModal();
      } else if (this.shouldOpenUserSummaryModal) {
        this.showUserActivationSummaryModal = true;
      }
    },

    openWelcomeEmailModal() {
      this.$options.ModalEventBus.open({
        component: WelcomeEmailConfirmationModal,
        componentProps: {
          defaultInputValue: this.item.email,
          cancelText: 'skip',
        },
      });
    },

    async saveAndBindDeviceBindings() {
      let saveReturnValue;

      /**
       * @description Update the password policy for the organization prior to
       * creating/updating the user. If the policy update fails, then we don't
       * want to save the other changes.
       */
      try {
        await this.updatePasswordPolicy();
        await this.handleSaveItem();
        await this.saveWebAuthnKeys();
        await this.savePublicKeys();
        await this.bindUserToUserGroups();

        const response = await this.bindUserToSystems();
        saveReturnValue = await this.parseBindSuccessMethod(response, ItemTypeConsts.device);

        await this.acknowledgeSelectedSystems();
        await this.bindSudoStatus();
        await this.bindUserToDirectories();
        await this.updateModifiedItemCount();
        this.openConfirmationModals();

        saveReturnValue.canContinue = !this.shouldOpenActivationEmailModal
            && !this.shouldOpenWelcomeEmailModal
            && !this.shouldOpenUserSummaryModal;

        return saveReturnValue;
      } catch (exp) {
        throw exp instanceof Error ? exp : Error('Unknown error');
      }
    },

    saveSuccess(item) {
      this.triggerSuccessNotification({
        icon: 'checkmarkCircle',
        title: this.isNewUser ? `${this.displayName()} saved successfully!` : 'User Saved',
        component: {
          template: this.isNewUser ? this.getMsgUserHasBeenSaved(item)
            : this.getMsgUserSettingsHasBeenSaved(item),
        },
      });
      // This is to show edit users settings notifcations if new user created and the user's state
      // is not same as defaultState
      if (!this.isNewUser && this.item.state !== this.defaultState) {
        this.triggerNotification({
          icon: 'info',
          title: 'Update User Settings',
          component: {
            template: this.getMsgUserSettingNotificationSaved(item),
          },
        });
      }
    },

    setUserBindingModalText() {
      const { binds, unbinds } = this.getSystemBindingChanges();
      const bindBody = 'Adding a device to a user will add a JumpCloud user to that device and allow that user to log in.';
      const unbindBody = 'Removing a device from a user will suspend that user\'s ability to log in, and in some cases, prevent the user from unlocking the device.';
      if (binds.length > 0 && unbinds.length > 0) {
        this.userModalTitle = 'Change Assigned Device';
        this.userModalBody = `${bindBody}`;
        this.userModalBody2 = `${unbindBody}`;
        this.userModalActionLabel = 'Confirm';
      } else if (binds.length > 0 && unbinds.length === 0) {
        this.userModalTitle = 'Bind Device to User';
        this.userModalBody = bindBody;
        this.userModalActionLabel = 'Bind Device';
      } else {
        this.userModalTitle = 'Unbind Device from User';
        this.userModalBody = unbindBody;
        this.userModalActionLabel = 'Unbind Device';
      }
    },
  },

  LocalStorageService,

};
</script>

<style module>
@value announcementZIndex from '~css/z-index.css';

.announcement {
  border-radius: 5px;
  color: var(--announcement-color);
  left: 0;
  padding: var(--jc-spacer-medium);
  position: absolute;
  top: 8rem;
  width: 40ch;
  z-index: announcementZIndex;
}

.avatarBadge {
  bottom: 0;
  height: 40px;
  left: calc(50% + 50px);
  position: absolute;
  transform: translateX(-50%);
  width: 40px;
}

.actionMenu {
  background-color: var(--jcPrimary);
  border: none;
  border-radius: 2px;
  margin-right: 15px;
}

.subTitle {
  margin-bottom: 3px !important;
  overflow-x: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
